import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, HEADER_PADDING_HORIZONTAL_SMALL, TYPOGRAPHY } from '@/styles';
import { FulfillmentActionSheet } from './fulfillment-action-sheet';
import { FulfillmentButton } from './fulfillment-button';
import { MultiStyleText } from '@/components/multi-style-text';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useNewOrderLoadingContext } from '@/contexts/new-order-loading-context';
import { useSupplierOrderAvailability } from '@/hooks/use-supplier-order-availability';
import { View } from 'react-native';

const AVAILABILITY_SPACING = 12;

export const FulfillmentAvailability: React.FC = () => {
  const { draftOrder, hasResponse, selectableFulfillmentOptions } =
    useNewOrderContext();
  const { selectedFulfillment, setNextFulfillment, updateAndClearQuantity } =
    useNewOrderActionsContext();
  const { isUpdatingQuantity } = useNewOrderLoadingContext();
  const { isSmallLayout } = useLayoutContext();
  const orderAvailability = useSupplierOrderAvailability();
  const [styles] = useStyles();
  const [showFulfillmentSelector, setShowFulfillmentSelector] = useState(false);

  const [cutoffText, minimumText] = useMemo(() => {
    return [
      orderAvailability.getCutoffForDate(selectedFulfillment?.date, true),
      orderAvailability.getOrderMinDisplayStringForDate(
        selectedFulfillment?.date,
      ),
    ];
  }, [orderAvailability, selectedFulfillment]);

  const emphasisTextStyle = [TYPOGRAPHY.small, FONTS.primaryBold];
  const hasConstraintContent = Boolean(minimumText || cutoffText);

  if (!selectedFulfillment || !hasResponse) {
    return null;
  }

  const selectionButton = (
    <FulfillmentButton
      fulfillmentDate={selectedFulfillment.date}
      fulfillmentType={selectedFulfillment.type}
      onPress={() => setShowFulfillmentSelector(true)}
      timeslot={selectedFulfillment.timeslot}
      trackingData={{
        name: 'fulfillment_availability.update_button',
      }}
    />
  );

  const styledMinimumText = minimumText ? (
    <MultiStyleText
      defaultStyle={TYPOGRAPHY.small}
      emphasisStyle={emphasisTextStyle}
    >
      {i18n.t('fulfillment_header.minimum', {
        minimumString: minimumText,
      })}
    </MultiStyleText>
  ) : null;

  const styledCutoffText = cutoffText ? (
    <MultiStyleText
      defaultStyle={TYPOGRAPHY.small}
      emphasisStyle={emphasisTextStyle}
    >
      {i18n.t('fulfillment_header.cutoff', {
        cutoffString: cutoffText,
      })}
    </MultiStyleText>
  ) : null;

  const fulfillmentSelector = showFulfillmentSelector ? (
    <FulfillmentActionSheet
      draftOrder={draftOrder}
      fulfillmentOptions={selectableFulfillmentOptions}
      initialSelectedFulfillment={selectedFulfillment!}
      isLoading={isUpdatingQuantity}
      onClose={() => setShowFulfillmentSelector(false)}
      onOpen={() => updateAndClearQuantity('fulfillment_action_sheet')}
      onSave={setNextFulfillment}
      trackingProperties={{
        source: 'fulfillment_availability_content_footer',
      }}
    />
  ) : null;

  if (isSmallLayout) {
    return (
      <View style={[styles.base, styles.baseSmall]}>
        <View style={styles.dateContainer}>{selectionButton}</View>
        {hasConstraintContent ? (
          <View style={styles.constraintContainer}>
            {styledMinimumText}
            {styledCutoffText}
          </View>
        ) : null}
        {fulfillmentSelector}
      </View>
    );
  }

  return (
    <View style={styles.base}>
      <View style={styles.dateContainer}>{selectionButton}</View>
      {styledMinimumText ? (
        <View style={styles.constraintTextLarge}>{styledMinimumText}</View>
      ) : null}
      {styledCutoffText ? (
        <View style={styles.constraintTextLarge}>{styledCutoffText}</View>
      ) : null}
      {fulfillmentSelector}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flexDirection: 'row',
    paddingBottom: 8,
  },
  baseSmall: {
    paddingHorizontal: HEADER_PADDING_HORIZONTAL_SMALL,
  },
  constraintContainer: {
    borderLeftColor: theme.colors.stroke,
    borderLeftWidth: 1,
    justifyContent: 'center',
    paddingLeft: AVAILABILITY_SPACING,
  },
  constraintTextLarge: {
    borderLeftColor: theme.colors.stroke,
    borderLeftWidth: 1,
    justifyContent: 'center',
    marginLeft: 12,
    paddingLeft: 12,
  },
  dateContainer: {
    justifyContent: 'center',
    marginRight: AVAILABILITY_SPACING,
  },
}));
